// Tax Calculation Utilities

// 2024 Federal Tax Brackets (Single Filer) - adjusted for inflation each year
const FEDERAL_TAX_BRACKETS_SINGLE = [
  { threshold: 0, rate: 0.10 },
  { threshold: 11600, rate: 0.12 },
  { threshold: 47150, rate: 0.22 },
  { threshold: 100525, rate: 0.24 },
  { threshold: 191950, rate: 0.32 },
  { threshold: 243725, rate: 0.35 },
  { threshold: 609350, rate: 0.37 }
];

// 2024 Federal Tax Brackets (Married Filing Jointly)
const FEDERAL_TAX_BRACKETS_MARRIED = [
  { threshold: 0, rate: 0.10 },
  { threshold: 23200, rate: 0.12 },
  { threshold: 94300, rate: 0.22 },
  { threshold: 201050, rate: 0.24 },
  { threshold: 383900, rate: 0.32 },
  { threshold: 487450, rate: 0.35 },
  { threshold: 731200, rate: 0.37 }
];

// Standard Deduction (2024)
const STANDARD_DEDUCTION = {
  single: 14600,
  married: 29200
};

// State Tax Rates (average for different cost areas)
const STATE_TAX_RATES = {
  'high-cost': 0.09,      // CA, NY, etc. (~9%)
  'medium-cost': 0.05,    // Average states (~5%)
  'low-cost': 0.03        // Low/no tax states (~3%)
};

// FICA (Social Security + Medicare)
const FICA_RATES = {
  socialSecurity: 0.062,  // 6.2% up to wage base
  medicare: 0.0145,       // 1.45% on all wages
  additionalMedicare: 0.009, // 0.9% on wages over threshold
  ssWageBase: 160200,     // 2024 SS wage base
  medicareSurtaxThreshold: {
    single: 200000,
    married: 250000
  }
};

/**
 * Calculate federal income tax using progressive brackets
 */
export const calculateFederalTax = (taxableIncome, isMarried = false, inflationMultiplier = 1) => {
  const brackets = isMarried ? FEDERAL_TAX_BRACKETS_MARRIED : FEDERAL_TAX_BRACKETS_SINGLE;
  let tax = 0;

  for (let i = 0; i < brackets.length; i++) {
    const bracket = brackets[i];
    const adjustedThreshold = bracket.threshold * inflationMultiplier;

    if (taxableIncome > adjustedThreshold) {
      const nextThreshold = i < brackets.length - 1
        ? brackets[i + 1].threshold * inflationMultiplier
        : Infinity;

      const taxableInThisBracket = Math.min(taxableIncome, nextThreshold) - adjustedThreshold;
      tax += taxableInThisBracket * bracket.rate;
    }
  }

  return tax;
};

/**
 * Calculate state income tax (simplified flat rate by region)
 */
export const calculateStateTax = (grossIncome, location = 'medium-cost') => {
  const rate = STATE_TAX_RATES[location] || STATE_TAX_RATES['medium-cost'];
  return grossIncome * rate;
};

/**
 * Calculate FICA taxes (Social Security + Medicare)
 */
export const calculateFICATax = (grossIncome, isMarried = false, inflationMultiplier = 1) => {
  const adjustedSSBase = FICA_RATES.ssWageBase * inflationMultiplier;
  const adjustedMedicareSurtaxThreshold = isMarried
    ? FICA_RATES.medicareSurtaxThreshold.married * inflationMultiplier
    : FICA_RATES.medicareSurtaxThreshold.single * inflationMultiplier;

  // Social Security (capped at wage base)
  const ssTax = Math.min(grossIncome, adjustedSSBase) * FICA_RATES.socialSecurity;

  // Medicare (no cap)
  const medicareTax = grossIncome * FICA_RATES.medicare;

  // Additional Medicare tax on high earners
  const additionalMedicare = grossIncome > adjustedMedicareSurtaxThreshold
    ? (grossIncome - adjustedMedicareSurtaxThreshold) * FICA_RATES.additionalMedicare
    : 0;

  return ssTax + medicareTax + additionalMedicare;
};

/**
 * Calculate total taxes and net income
 */
export const calculateNetIncome = (grossIncome, options = {}) => {
  const {
    isMarried = false,
    location = 'medium-cost',
    retirement401kContribution = 0,
    inflationMultiplier = 1,
    otherDeductions = 0
  } = options;

  // Calculate taxable income
  const standardDeduction = isMarried
    ? STANDARD_DEDUCTION.married * inflationMultiplier
    : STANDARD_DEDUCTION.single * inflationMultiplier;

  const taxableIncome = Math.max(0,
    grossIncome - retirement401kContribution - standardDeduction - otherDeductions
  );

  // Calculate all taxes
  const federalTax = calculateFederalTax(taxableIncome, isMarried, inflationMultiplier);
  const stateTax = calculateStateTax(grossIncome, location); // State tax on gross income (simplified)
  const ficaTax = calculateFICATax(grossIncome, isMarried, inflationMultiplier);

  const totalTax = federalTax + stateTax + ficaTax;
  const netIncome = grossIncome - totalTax;

  return {
    grossIncome,
    federalTax,
    stateTax,
    ficaTax,
    totalTax,
    netIncome,
    effectiveTaxRate: totalTax / grossIncome,
    taxableIncome
  };
};

/**
 * Calculate tax savings from 401k contribution
 */
export const calculate401kTaxSavings = (contribution, marginalTaxRate, stateTaxRate, ficaRate = 0.0765) => {
  // Federal + State + FICA savings
  return contribution * (marginalTaxRate + stateTaxRate + ficaRate);
};
